import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Table, TableProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';

// components
import NoData from 'components/noData/noData';
import ListButton from 'components/listButton/listButton';
import ActionListHeaderComponent from '../../listHeader';
import LoadingComponent from 'components/loadingComponent/loadingComponent';

// services

// styles

// models
import { ShareholderInfo } from '../models/shareholderInfo';
import { onGetShareholdersInfoList } from '../services/shareholderInfoServices';
import NotificationService from 'core/services/notification';
import TableTotal from 'components/tableTotal/tableTotal';
import { isRTL } from './../../../../../core/services/language';
import { dateFormat } from 'utils/dateFormat';
import axios from 'axios';
import { downloadFile } from 'utils/downloadFile';
import { onGetServiceCost } from '../../commonServices';
import { ServiceCost } from '../../commonModels';

interface ShareholderInfoListPageProps {}

const ShareholderInfoListPage: FunctionComponent<ShareholderInfoListPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [requests, setRequests] = useState<ShareholderInfo[]>([]);

	const [serviceCostLoading, setServiceCostLoading] = useState<boolean>(true);
	const [serviceCost, setServiceCost] = useState<ServiceCost>();

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps<ShareholderInfo>['columns'] = [
		{
			title: t('ACTIONS.REQUEST_CODE'),
			dataIndex: 'id',
			key: 'id',
			render: (id) => <>#{id}</>,
		},
		{
			title: t('ACTIONS.REQUEST_DATE'),
			dataIndex: 'created_at',
			key: 'created_at',
		},
		{
			title: t('ACTIONS.SHAREHOLDER_NATIONAL_ID'),
			dataIndex: 'shareholder',
			key: 'shareholder_national_id',
			render: (shareholder) => shareholder.national_id,
		},
		{
			title: t('ACTIONS.SHAREHOLDER_NAME'),
			dataIndex: 'shareholder',
			key: 'shareholder_name',
			render: (shareholder) => (isRTL() ? shareholder.name : shareholder.name_en ?? shareholder.name),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => (
				<Flex gap={15} align="center" justify="center">
					<ListButton type="view" onClick={() => navigate(`/dashboard/actions/shareholder-info/${record.id}`)} />
					<ListButton type="download" onClick={() => downloadFile(record.certificate_url.download_link, record.certificate_url.title)} />
				</Flex>
			),
		},
	];

	useEffect(() => {
		setCurrentPage(getCurrentPage());
		getShareholdersInfo();
		getServiceCost();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getServiceCost = async () => {
		try {
			setServiceCostLoading(true);

			const response = await onGetServiceCost('shareholder_information');
			setServiceCost(response?.success);

			setServiceCostLoading(false);
		} catch (error) {
			setServiceCostLoading(false);
		}
	};

	const getShareholdersInfo = async () => {
		try {
			setIsLoading(true);

			const response = await onGetShareholdersInfoList(getCurrentPage());

			setRequests(response.shareholder_information_actions);
			setTotalRecords(response.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetShareholdersInfoList(getCurrentPage());
			setRequests(response.shareholder_information_actions);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	return (
		<>
			<ActionListHeaderComponent
				title={t('ACTIONS.SHAREHOLDER_INFO')}
				subTitle={t('ACTIONS.VIEW_SHAREHOLDERS')}
				customNavigate={() => navigate('/dashboard/actions/shareholder-info/create')}
				costLoading={serviceCostLoading}
				costs={[serviceCost!]}
			/>

			<Table
				columns={columns}
				dataSource={requests}
				scroll={{ x: 600 }}
				rowKey="id"
				loading={isLoading}
				className="bordered"
				pagination={{
					pageSize: 15,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
				locale={{ emptyText: <NoData /> }}
			/>
		</>
	);
};

export default ShareholderInfoListPage;
